import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { BadgeCheck, ShieldX } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Card, CardBody } from "@/components/ui/Card";
import { LoadingPanel } from "@/components/ui/Feedback";
import { formatDate } from "@/components/ui/DataTable";

const cardSchema = z.object({
  valid: z.boolean(),
  patient: z.object({
    rm_number: z.string(),
    name: z.string(),
    dob: z.string().nullable().default(null),
    gender: z.string().nullable().default(null),
  }),
});

/**
 * Patient card verification.
 *
 * Opened by scanning the QR on a patient card, usually on a counter device
 * that is not signed in — so this route is public by design. The token is
 * signed and expires after 24 hours; an invalid one says only that, never
 * whether the record exists.
 */
export function CardVerify() {
  const { token } = useParams<{ token: string }>();

  const { data, isPending, error } = useQuery({
    queryKey: ["patient-card", token],
    queryFn: ({ signal }) => api.get(`/api/patient-card/${token}`, cardSchema, signal),
    enabled: Boolean(token),
    retry: false,
  });

  return (
    <main className="bg-canvas grid min-h-dvh place-items-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex items-center gap-2.5">
          <span
            aria-hidden
            className="bg-accent text-accent-content font-display grid size-9 place-items-center text-lg font-extrabold"
            style={{ borderRadius: "0.75rem 0.25rem 0.75rem 0.25rem" }}
          >
            M
          </span>
          <span className="font-display text-lg leading-none font-extrabold tracking-tight">
            mediora
          </span>
        </div>

        <p className="eyebrow mb-1.5">Verifikasi kartu pasien</p>

        {isPending ? (
          <Card>
            <LoadingPanel label="Memeriksa kartu" />
          </Card>
        ) : error || !data ? (
          <Card className="border-critical-500/40">
            <CardBody className="flex flex-col items-center py-10 text-center">
              <span className="bg-critical-tint mb-3 grid size-12 place-items-center rounded-full">
                <ShieldX className="text-critical-text size-6" />
              </span>
              <p className="font-display text-lg font-bold">Kartu tidak valid</p>
              <p className="text-content-muted mt-1 text-sm">
                {error instanceof ApiError
                  ? error.message
                  : "Kartu tidak dikenali atau sudah kedaluwarsa."}
              </p>
              <p className="text-content-subtle mt-3 text-xs">
                Minta pasien membuka ulang kartu digitalnya di Portal Pasien.
              </p>
            </CardBody>
          </Card>
        ) : (
          <Card className="border-routine-500/40">
            <CardBody className="space-y-5">
              <div className="flex flex-col items-center text-center">
                <span className="bg-routine-tint mb-3 grid size-12 place-items-center rounded-full">
                  <BadgeCheck className="text-routine-text size-6" />
                </span>
                <p className="font-display text-lg font-bold">Kartu terverifikasi</p>
                <p className="text-content-muted mt-1 text-sm">
                  Cocokkan data di bawah dengan identitas pasien.
                </p>
              </div>

              <dl className="divide-hairline border-hairline divide-y border-y text-sm">
                {[
                  ["No. rekam medis", data.patient.rm_number],
                  ["Nama", data.patient.name],
                  ["Tanggal lahir", formatDate(data.patient.dob)],
                  ["Jenis kelamin", data.patient.gender ?? "—"],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 py-2.5">
                    <dt className="text-content-subtle shrink-0">{label}</dt>
                    <dd className="tabular min-w-0 truncate text-right font-semibold">{value}</dd>
                  </div>
                ))}
              </dl>

              <p className="text-content-subtle text-xs">
                Verifikasi ini hanya memastikan kartu asli dan belum kedaluwarsa. Untuk tindakan
                medis, tetap konfirmasi identitas sesuai prosedur rumah sakit.
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </main>
  );
}
